// Pull-to-refresh hook for touch devices.
//
// Detects a downward drag gesture near the top of a scrollable container.
// Once the user pulls past `threshold` px and releases, `onRefresh` is
// called. While pulling past threshold, `pulling` becomes true so the
// caller can render a "release to refresh" indicator.
//
// Limitation: only works inside a container that's already at scrollTop=0;
// outside that, the gesture is ignored (no conflict with normal scrolling).
import { useState, useRef, useCallback, useEffect } from 'react';

export default function usePullToRefresh(onRefresh, { threshold = 80 } = {}) {
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const distanceRef = useRef(0);

  const onTouchStart = useCallback((e) => {
    // Only initiate pull when scrolled to top.
    if (window.scrollY > 5) {
      startYRef.current = null;
      return;
    }
    startYRef.current = e.touches[0].clientY;
    distanceRef.current = 0;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    // Only track downward swipes; dampen with 2× divisor to feel less twitchy.
    const pulled = Math.max(0, Math.min(dy * 0.5, threshold * 1.5));
    distanceRef.current = pulled;
    if (pulled > 8) setPulling(true);
  }, [threshold]);

  const onTouchEnd = useCallback(async () => {
    if (startYRef.current == null) return;
    const pulled = distanceRef.current;
    startYRef.current = null;
    distanceRef.current = 0;
    setPulling(false);
    if (pulled >= threshold && !refreshing) {
      setRefreshing(true);
      try { await onRefresh(); }
      finally { setRefreshing(false); }
    }
  }, [threshold, refreshing, onRefresh]);

  useEffect(() => () => { /* cleanup if needed */ }, []);

  return { pulling, refreshing, distance: distanceRef.current, onTouchStart, onTouchMove, onTouchEnd };
}